import React, { useState } from "react";
import { motion } from "framer-motion"
import { Box, Typography, Paper, Grid, Button, Divider, Chip, Fade, Container } from "@mui/material";
import TokenSwapInput from "../components/TokenSwapInput";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const antlersImg = "/images/token_antlers.png";
const deeriumImg = "/images/Token_Deerium.png"; // Updated to new Deerium token icon

const glassBg = {
    background: "rgba(44, 32, 16, 0.65)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "2px solid rgba(245, 225, 163, 0.18)",
    boxShadow: "0 4px 16px 0 rgba(31, 38, 135, 0.22), 0 0 12px 1px #f5e1a3a0",
};

const Swap = () => {
    const [antlersAmount, setAntlersAmount] = useState(0);
    const [deeriumAmount, setDeeriumAmount] = useState(0);
    const [swapping, setSwapping] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [animatedDeerium, setAnimatedDeerium] = useState(0);

    // Placeholder balances (replace with real data if available)
    const antlersBalance = 10;
    const deeriumBalance = 0;

    // 1:1 swap logic
    const handleAntlersChange = (e: any) => {
        const value = e.target.value.replace(/[^0-9.]/g, "");
        setAntlersAmount(value === "" ? 0 : Number(value));
        setDeeriumAmount(value === "" ? 0 : Number(value));
    };

    const handleSwap = () => {
        setSwapping(true);
        setShowConfetti(false);
        let end = antlersAmount;
        let duration = 900;
        let startTime: number | null = null;
        const animate = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setAnimatedDeerium(Math.floor(progress * end));
            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                setSwapping(false);
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 1200);
            }
        };
        requestAnimationFrame(animate);
    };

    const isSwapDisabled = antlersAmount <= 0 || antlersAmount > antlersBalance || swapping;
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 2, md: 4 } }}>
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
            >
                <Paper
                    elevation={3}
                    sx={{
                        pl: { xs: 2, sm: 3, md: 4 },
                        pr: 0,
                        py: { xs: 1.5, sm: 2 },
                        mb: 2,
                        background: "#4a300b",
                        borderRadius: "16px",
                        position: "relative",
                        overflow: "hidden",
                        border: "6px solid #7d5014",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                        minHeight: 0,
                        '::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 0,
                            pointerEvents: 'none',
                            background: 'linear-gradient(120deg, rgba(255,233,163,0.08) 0%, rgba(125,80,20,0.10) 60%, rgba(255,233,163,0.08) 100%)',
                            animation: 'swapShimmer 6s linear infinite',
                            backgroundSize: '200% 200%',
                        },
                    }}
                >
                    <style>{`
            @keyframes swapShimmer {
              0% { background-position: 0% 50%; }
              100% { background-position: 100% 50%; }
            }
          `}</style>
                    {/* Faded, tilted swap icon at right */}
                    <Box
                        sx={{
                            position: "absolute",
                            right: 24,
                            top: '50%',
                            transform: "translateY(-50%)",
                            opacity: 0.5,
                            zIndex: 1,
                            height: '80%',
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: "none",
                            filter: 'blur(0.2px)',
                        }}
                    >
                        <img src={deeriumImg} alt="Deerium" style={{ height: '100%', maxHeight: 260, filter: 'drop-shadow(0 0 32px #ffe9a3aa)' }} />
                    </Box>
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            minHeight: 80,
                        }}
                    >
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="white"
                            sx={{ letterSpacing: 1, textShadow: "1px 1px 6px #3a2609", mb: 0.5, fontSize: { xs: '1.25rem', sm: '2rem' }, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <DoubleArrowIcon sx={{ fontSize: { xs: 28, sm: 34 }, color: '#ffe9a3', mr: 1 }} />
                            TOKEN SWAP
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            color="#ffe9a3"
                            sx={{ fontWeight: 500, opacity: 0.95, fontSize: { xs: '0.98rem', sm: '1rem' } }}
                        >
                            Instantly migrate your legacy ANTLERS to DEERIUM
                        </Typography>
                        <Typography
                            variant="caption"
                            color="#ffe9a3"
                            sx={{ mt: 1.2, opacity: 0.7, fontSize: { xs: '0.80rem', sm: '0.92rem' }, fontStyle: 'italic', letterSpacing: 0.1, display: 'block', borderTop: '1px solid #ffe9a355', pt: 1.2 }}
                        >
                            Disclaimer: This migration is <b>permanent</b> and cannot be reversed. Please double-check the amount before swapping.
                        </Typography>
                    </Box>
                </Paper>
            </motion.div>
            {/* Swap Action Card */}
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                style={{ marginTop: 0, paddingTop: 0 }}
            >
                    <Box
                        sx={{
                            maxWidth: 370,
                            mx: 'auto',
                            mt: 4,
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: '18px',
                            background: 'rgba(44, 32, 16, 0.72)',
                            boxShadow: '0 4px 24px 0 rgba(31, 38, 135, 0.18), 0 0 12px 1px #f5e1a355',
                            border: '8px solid #5c3c10',
                            position: 'relative',
                            zIndex: 3,
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', mb: 1, gap: 1 }}>
                            <img src={antlersImg} alt="Antlers" style={{ width: 28, marginRight: 6, filter: 'drop-shadow(0 0 4px #ffe9a3)' }} />
                            <ArrowForwardIcon sx={{ color: '#ffe9a3', fontSize: 22, mx: 1, filter: 'drop-shadow(0 0 3px #ffe9a3)' }} />
                            <img src={deeriumImg} alt="Deerium" style={{ width: 28, filter: 'drop-shadow(0 0 4px #ffe9a3)' }} />
                        </Box>
                        <Typography variant="h6" align="center" sx={{ color: '#f5e1a3', fontWeight: 700, mb: 0.5, fontSize: '1.08rem', letterSpacing: 0.5, textShadow: '0 1px 4px #000' }}>
                            Swap ANTLERS → DEERIUM
                        </Typography>
                        <Chip label="Migration • Permanent" size="small" sx={{ bgcolor: '#2e1a07', color: '#ffe9a3', fontWeight: 600, mb: 1, fontSize: '0.75rem', letterSpacing: 0.2, boxShadow: '0 0 4px #ffe9a3a0' }} />
                        <Typography align="center" sx={{ color: '#ffe9a3', mb: 1, fontSize: '0.92rem', fontWeight: 400, opacity: 0.92 }}>
                            Upgrade your legacy ANTLERS to the all-new DEERIUM token.
                        </Typography>
                        <Divider sx={{ borderColor: 'rgba(245, 225, 163, 0.18)', mb: 1 }} />
                        <Grid container spacing={1.2} direction="column" alignItems="center">
                            <Grid item xs={12} width="100%">
                                <TokenSwapInput
                                    value={antlersAmount}
                                    onChange={handleAntlersChange}
                                    token={"ANTLERS"}
                                    img={antlersImg}
                                    type="sell"
                                    balance={`${antlersBalance.toLocaleString()} ANTLERS`}
                                    onMax={() => setAntlersAmount(antlersBalance)}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Box sx={{ p: 0.5, borderRadius: '50%', bgcolor: 'rgba(245, 225, 163, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 6px #ffe9a3a0' }}>
                                    <ArrowDownwardIcon sx={{ color: '#f5e1a3', fontSize: 16 }} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} width="100%">
                                <TokenSwapInput
                                    value={swapping ? animatedDeerium : deeriumAmount}
                                    token={"DEERIUM"}
                                    img={deeriumImg}
                                    type="receive"
                                    balance={`${deeriumBalance.toLocaleString()} DEERIUM`}
                                />
                            </Grid>
                            <Grid item xs={12} width="100%" sx={{ mt: 0.5 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSwap}
                                    disabled={true}
                                    size="medium"
                                    sx={{
                                        fontWeight: 600,
                                        py: 1,
                                        fontSize: '0.98rem',
                                        background: 'rgba(0,0,0,0.2)',
                                        color: '#ffe9a3',
                                        boxShadow: 'none',
                                        border: '1px solid #ffe9a3',
                                        borderRadius: '10px',
                                        letterSpacing: 0.5,
                                        transition: 'all 0.22s cubic-bezier(.4,2,.3,1)',
                                    }}
                                >
                                    Swap Coming Soon
                                </Button>
                            </Grid>
                        </Grid>
                        <Typography align="center" sx={{ color: 'red', mt: 2, fontSize: '0.92rem', fontWeight: 'bold' }}>
                            *Swap coming soon.
                        </Typography>
                    </Box>
            </motion.div>
        </Container>
    );
};

export default Swap; 